






















import { Component, Prop, Vue } from 'vue-property-decorator'
import { namespace } from 'vuex-class'
import KYCForm from '@master/model/kyc-form/KYCForm'
import AppDialog from '@core/components/AppDialog.vue'
import AppNewClientManual from '@core/components/AppNewClientManual.vue'
import { container, cid, inject } from 'inversify-props'
import API from '@core/services/API'
import { IRiskRatingService } from '@master/services/IRiskRatingService'
import { IProjectSettings } from '@master/config/IProjectSettings'
import { IDateService } from '@master/services/IDateService'
import { UIService } from '@core/services/UIService'
import { SharedVariable } from '@core/model/SharedVariable'
import { cloneDeep } from 'lodash/fp'
import FullPersonalDetails from '@master/model/kyc-form/PersonalDetails'
import { Tree } from 'element-ui'
import { IPrioritiesStructure } from '@master/model/kyc-form/need-analysis/IPrioritiesStructure'
import { get, set } from 'lodash'
import KYCClient from '@master/model/kyc-form/KYCClient'

const kycFormStore = namespace('KYCFormStore')
const kycSectionStore = namespace('KYCSectionStore')
const dateService = container.get<IDateService>(cid.IDateService)
const projectSettings = container.get<IProjectSettings>(cid.IProjectSettings)
const riskRatingService = container.get<IRiskRatingService>(cid.IRiskRatingService)

@Component({
  components: {
    AppDialog,
    AppNewClientManual
  }
})
export default class AppCloneForm extends Vue {
  @Prop() srcSectionMap
  @Prop() additionalExcludedSection?: string []
  @kycSectionStore.State
  private sections

  @kycFormStore.State
  private kycForm!: KYCForm

  @inject()
  private $api: API

  uiService: UIService

  @inject()
  private sharedVariable: SharedVariable

   @inject()
  private ThePrioritiesNeeds: IPrioritiesStructure<KYCClient>

  private newKycForm: KYCForm = new KYCForm(dateService, projectSettings, riskRatingService)
  private clonedSections = []
  protected defaultCheckedKeys = ['about-you', 'our-recommendation'] // default checked tree
  protected excludedSections = ['disclaimers-and-client-checklist', 'client-declaration', 'financial-consultant-declaration', 'kyc-confirmation', 'appendix']

  private kycPersonalDetails = {
    client1: new FullPersonalDetails('client_0', dateService),
    client2: new FullPersonalDetails('client_1', dateService)
  }

  get sectionsMapping () {
    return this.srcSectionMap || this.defaultSectionsMapping
  }

  get defaultSectionsMapping () {
    return {
      'client-summary': [
        'client1.personalDetails',
        'client2.personalDetails',
        'clientDeclaration'
      ],
      'important-notices': [
        'clientDeclaration'
      ],
      'personal-details': [
        'client1.personalDetails',
        'client2.personalDetails'
      ],
      dependants: [
        'client1.personalDetails',
        'client2.personalDetails',
        'dependants'
      ],
      'client-accompaniment': [
        'trustedIndividual',
        'client1.personalDetails',
        'client2.personalDetails'
      ],
      'ai-oi': [
        'client1.aioi',
        'client2.aioi'
      ],
      'property-financing': [
        'client1.propertyFinancing'
      ],
      'risk-profile-assessment': [
        'client1.rpa',
        'client2.rpa'
      ],
      'customer-knowledge-assessment': [
        'client1.cka',
        'client2.cka'
      ],
      'cash-flow': [
        'client1.cashFlow',
        'client2.cashFlow'
      ],
      'net-worth': [
        'client1.netWorth',
        'client2.netWorth'
      ],
      budget: [
        'client1.budget',
        'client2.budget',
        'client1.thirdPartyPayor',
        'client2.thirdPartyPayor'
      ],
      insurance: [
        'existingPortfolio'
      ],
      investment: [
        'existingInvestment'
      ],
      'identifying-your-priorities': [
        'clientDeclaration',
        ...this.priorities('client1'),
        ...this.priorities('client2')
      ],
      'needs-analysis-calculation': [
        ...this.priorities('client1'),
        ...this.priorities('client2')
      ],
      'adjustments-and-assumptions': [
        'clientAssumptions'
      ],
      'our-recommendation': [
        'recommendations',
        'ilpPortfolios',
        'rpa',
        'client1.recommendationBasis',
        'client2.recommendationBasis',
        'clientDeclaration',
        'client1.personalDetails',
        'client2.personalDetails'
      ],
      'replacement-or-switching': [
        'client1.switching',
        'client2.switching',
        'adviser.declaration'
      ],
      bop: [
        'client1.beneficialOwner',
        'client2.beneficialOwner'
      ],
      pep: [
        'clientDeclaration'
      ],
      ecdd: [
        'client1.ecdd',
        'client2.ecdd',
        'client1.personalDetails',
        'client2.personalDetails'
      ]
    }
  }

  get currentUser () {
    return this.$store.getters.currentUser
  }

  priorities (client: 'client1' | 'client2') {
    return this.ThePrioritiesNeeds.getPriorities().map(priority => `${client}.${priority.key}`) || []
  }

  assignAllKYCForm () {
    // Get all checked sections & subsections tree, and filtered only the subsections or our-recommendations
    const sectionTree = this.$refs.sectionTree as Tree
    const checkedSections = sectionTree.getCheckedNodes().filter(section => !section.children || section.children.length === 0)

    // Get all sections models based on the sectionsMapping
    const clonedModels = []
    checkedSections.forEach(section => {
      const sectionModels = this.sectionsMapping[section.id]
      clonedModels.push(...sectionModels)
    })

    // Removed duplicated models
    const removedDuplicatedModels = [...new Set(clonedModels)]

    // Assign new KYC Form based on the selected sections
    removedDuplicatedModels.forEach(model => {
      const toCopy = get(this.kycForm, model)
      set(this.newKycForm, model, toCopy)
    })

    // Assign new kycform with the kyc client
    this.newKycForm.client1.personalDetails.title = cloneDeep(this.kycPersonalDetails.client1.title)
    this.newKycForm.client1.personalDetails.name = cloneDeep(this.kycPersonalDetails.client1.name)
    this.newKycForm.client1.personalDetails.nric = cloneDeep(this.kycPersonalDetails.client1.nric)
    this.newKycForm.client1.personalDetails.contactMobile = cloneDeep(this.kycPersonalDetails.client1.contactMobile)

    if (this.newKycForm.client2.personalDetails.included || this.kycPersonalDetails.client2.name) {
      this.newKycForm.client2.personalDetails.included = true
      this.newKycForm.client2.personalDetails.title = cloneDeep(this.kycPersonalDetails.client2.title)
      this.newKycForm.client2.personalDetails.name = cloneDeep(this.kycPersonalDetails.client2.name)
      this.newKycForm.client2.personalDetails.nric = cloneDeep(this.kycPersonalDetails.client2.nric)
      this.newKycForm.client2.personalDetails.contactMobile = cloneDeep(this.kycPersonalDetails.client2.contactMobile)
    }

    // Assign adviser oid
    this.newKycForm.adviser.details.adviserOid = this.currentUser.accountID
  }

  async saveKYCForm () {
    const rawKycForm = this.newKycForm.convertToRawData()
    const response = await this.$api.kycForm.meta({
      _oid: 'new',
      meta: JSON.parse(JSON.stringify(rawKycForm))
    })

    this.$emit('close')
    const isConfirm = await this.uiService.confirm.show({
      type: 'success',
      message: 'KYC Form cloned successfully! Do you wish to go to the new KYC Form?'
    })
    if (isConfirm) {
      window.location.href = '/live/kyc-form/' + response.updateMeta._oid
    }
  }

  async cloneKYCForm () {
    await this.assignAllKYCForm()
    await this.saveKYCForm()
  }

  initSectionTree () {
    const excludeBranches = this.excludedSections.concat(this.additionalExcludedSection)
    this.clonedSections =
      this.sections
        .filter(section => !excludeBranches.includes(section.url) && section.url) // excludes some sections & only include route with valid url
        .map(section => {
          const children = section.subs ? section.subs.map(({ name, url }) => ({ label: name, id: url })) : []
          return {
            label: section.name,
            children,
            id: section.url
          }
        })
  }

  initKYCForm () {
    this.kycPersonalDetails.client1 = cloneDeep(this.kycForm.client1.personalDetails)
    if (this.kycForm.client2.personalDetails.included) {
      this.kycPersonalDetails.client2 = cloneDeep(this.kycForm.client2.personalDetails)
    }
  }

  mounted () {
    this.uiService = this.sharedVariable.uiService
    this.initSectionTree()
    this.initKYCForm()
  }
}
